import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { isMobileApp, openWebappWithAuth } from '../utils/platform';
import SEOHead from './SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BuyCreditsPage = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [fetchingPackages, setFetchingPackages] = useState(true);

  useEffect(() => {
    console.log('BuyCreditsPage mounted, BACKEND_URL:', BACKEND_URL);
    fetchPackages();
  }, []);

  // Auto-trigger 100 credit purchase if coming from low credit modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const autoTrigger = urlParams.get('auto');
    
    if (autoTrigger === '100' && packages.length > 0 && !loading) {
      // Find the 100 credit package (small)
      const smallPackage = packages.find(pkg => pkg.id === 'small' || pkg.credits === 100);
      if (smallPackage) {
        console.log('Auto-triggering 100 credit purchase');
        handlePurchase(smallPackage);
      }
    }
  }, [packages]);

  const fetchPackages = async () => {
    try {
      setFetchingPackages(true);
      console.log('Fetching packages from:', `${BACKEND_URL}/api/credits/packages`);
      const response = await fetch(`${BACKEND_URL}/api/credits/packages`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Packages response:', data);
      
      if (data.success && data.packages) {
        console.log('Setting packages:', data.packages.length, 'packages');
        setPackages(data.packages);
      } else {
        console.error('Failed to fetch packages - no success flag or packages missing');
        toast.error('Failed to load credit packages');
      }
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error(`Failed to load credit packages: ${error.message}`);
    } finally {
      setFetchingPackages(false);
    }
  };

  const handlePurchase = async (pkg) => {
    // On mobile app: open website in browser with auto-login, no Razorpay inside app
    if (isMobileApp()) {
      toast.info('Opening website to complete purchase...');
      await openWebappWithAuth('/buy-credits');
      setLoading(false);
      return;
    }

    console.log('handlePurchase called with package:', pkg);
    setLoading(true);
    setSelectedPackage(pkg.id);

    try {
      const token = localStorage.getItem('token');
      console.log('Token available:', !!token);
      
      if (!token) {
        toast.error('Please login to purchase credits');
        setLoading(false);
        setSelectedPackage(null);
        return;
      }
      
      // Create order
      console.log('Creating order for package:', pkg.id);
      const orderResponse = await fetch(`${BACKEND_URL}/api/credits/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          package: pkg.id
        })
      });

      const orderData = await orderResponse.json();
      console.log('Order response:', orderData);

      if (!orderData.success) {
        throw new Error(orderData.detail || 'Failed to create order');
      }
      
      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        throw new Error('Razorpay library not loaded. Please refresh the page.');
      }

      // Razorpay payment
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.order_id,
        name: 'Nerd AI',
        description: `${orderData.credits} Nerd-AI Credits`,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await fetch(`${BACKEND_URL}/api/credits/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              toast.success(`Successfully purchased ${orderData.credits} credits!`);
              window.dispatchEvent(new Event('creditUpdated')); // Trigger live update
              navigate('/');
            } else {
              toast.error('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Failed to verify payment');
          } finally {
            setLoading(false);
            setSelectedPackage(null);
          }
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            setSelectedPackage(null);
          }
        },
        theme: {
          color: '#9333ea'
        }
      };

      console.log('Opening Razorpay modal with options:', options);
      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.message || 'Failed to initiate purchase');
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <SEOHead title="Buy AI Credits" description="Top up your Nerd Nation AI credits to access career advice, exam prep, scholarship matching and more AI-powered tools." path="/buy-credits" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Buy Nerd-AI Credits
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto">
            Power your learning journey with AI. Choose a package that fits your needs.
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
          {fetchingPackages ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-600">Loading credit packages...</p>
            </div>
          ) : packages.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-red-600">No credit packages available</p>
            </div>
          ) : (
            packages.map((pkg) => (
            <Card
              key={pkg.id}
              className="border-2 border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all duration-300 cursor-pointer bg-white"
            >
              <div className="p-4">
                <div className="text-center">
                  {/* Credit Icon */}
                  <div className="flex justify-center mb-3">
                    <div className="relative">
                      <svg className="w-10 h-10 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.3"/>
                      </svg>
                      <svg className="w-5 h-5 text-yellow-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="text-2xl font-bold text-amber-900 mb-1">
                    {pkg.credits.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-600 mb-3">Credits</div>
                  
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-3 mb-3 border border-amber-200">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">Base Price</span>
                      <span className="font-semibold">₹{pkg.base_price.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-gray-600">GST (18%)</span>
                      <span className="font-semibold">₹{pkg.gst.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-amber-300 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-bold text-gray-900 text-sm">Total</span>
                        <span className="font-bold text-amber-600 text-base">
                          ₹{pkg.total.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => handlePurchase(pkg)}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-2 text-sm"
                  >
                    {loading && selectedPackage === pkg.id ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      <>
                        <CreditCard className="w-3 h-3 mr-1 inline" />
                        Buy Now
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          )))}

        </div>

        {/* Info Section */}
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
            <div className="p-4 sm:p-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="relative">
                    <svg className="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.3"/>
                    </svg>
                    <svg className="w-3 h-3 text-yellow-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-amber-900 mb-2 sm:mb-3">How to use credits:</h3>
                  <ul className="space-y-1.5 sm:space-y-2 text-amber-900 text-sm">
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Ask Nerd AI (Exam Practice):</strong> 3 credits per query</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Ask Nerd AI (Dashboard):</strong> 2 credits per query</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Research Paper Analysis:</strong> 70 credits per paper</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Credits valid for 30 days</strong> from purchase</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>1 INR = 1 Credit</strong> (plus 18% GST)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Back Button */}
        <div className="text-center mt-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 text-sm"
          >
            ← Back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BuyCreditsPage;
